import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { useLang } from "@/context/LanguageContext";
import { fmtRM, fmtRMSigned, fmtMonth } from "@/lib/format";
import { Download, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const COLS = [
  { key: "income", label: "Income", cls: "text-green-600" },
  { key: "principal", label: "Principal", cls: "text-slate-800" },
  { key: "inCapital", label: "In Capital", cls: "text-slate-800" },
  { key: "totalIn", label: "Total In", cls: "text-slate-900 font-semibold", group: true },
  { key: "expenses", label: "Expenses", cls: "text-red-600" },
  { key: "newLoan", label: "New Loan", cls: "text-amber-600" },
  { key: "commission", label: "Commission", cls: "text-slate-800" },
  { key: "outCapital", label: "Out Capital", cls: "text-slate-800" },
  { key: "tepi", label: "Tepi", cls: "text-slate-800" },
  { key: "totalOut", label: "Total Out", cls: "text-slate-900 font-semibold", group: true },
];

export default function MonthlyIncome() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = ["director", "manager", "admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [mode, setMode] = useState("excel"); // "excel" (Summary tab) | "computed" (admin)

  useEffect(() => {
    setLoading(true);
    api.get("/reports/monthly-income", { params: { mode } })
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [mode]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/monthly-income/export", { params: { mode }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "monthly_income.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const years = useMemo(
    () => [...new Set(rows.map((r) => (r.month || "").slice(0, 4)).filter(Boolean))].sort().reverse(),
    [rows]
  );

  const filteredRows = year === "all" ? rows : rows.filter((r) => (r.month || "").startsWith(year));

  const sumKeys = ["income", "principal", "inCapital", "totalIn", "expenses", "newLoan", "commission", "outCapital", "tepi", "totalOut", "netCash", "netProfit"];
  const totals = filteredRows.reduce((acc, r) => {
    sumKeys.forEach((k) => { acc[k] = (acc[k] || 0) + (r[k] || 0); });
    return acc;
  }, {});

  const num = (v, cls) => (
    <td className={`px-3 py-3 text-right font-mono-fig whitespace-nowrap ${cls}`}>{fmtRM(v || 0)}</td>
  );

  return (
    <Layout>
      <PageHeader
        title={t("mi.title")}
        subtitle={mode === "computed"
          ? "Computed live from payments & ledger (Admin view)"
          : "Driven directly from your branch's Summary (Excel) tab"}
        action={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid="monthly-mode-toggle">
                <button
                  data-testid="monthly-mode-excel"
                  onClick={() => setMode("excel")}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${mode === "excel" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  Excel (Summary)
                </button>
                <button
                  data-testid="monthly-mode-computed"
                  onClick={() => setMode("computed")}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${mode === "computed" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                >
                  Computed (Admin)
                </button>
              </div>
            )}
            <select
              data-testid="monthly-year-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="py-2 pl-3 pr-8 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 focus:ring-2 focus:ring-slate-900 outline-none cursor-pointer"
            >
              <option value="all">{t("mi.all_years")}</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              data-testid="monthly-export-button"
              onClick={exportXlsx}
              disabled={exporting || rows.length === 0}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white hover:bg-slate-800 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export Excel
            </button>
          </div>
        }
      />

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="monthly-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3 font-medium sticky left-0 bg-slate-50">Month</th>
              {COLS.map((c) => (
                <th key={c.key} className={`px-3 py-3 font-medium text-right whitespace-nowrap ${c.group ? "bg-slate-100" : ""}`}>{c.label}</th>
              ))}
              <th className="px-3 py-3 font-medium text-right bg-slate-100">{t("mi.net_cash")}</th>
              <th className="px-5 py-3 font-medium text-right bg-slate-100">Profit</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r) => {
              const profit = r.netProfit ?? 0;
              return (
                <tr key={r.month} data-testid={`monthly-row-${r.month}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900 sticky left-0 bg-white whitespace-nowrap">{fmtMonth(r.month)}</td>
                  {COLS.map((c) => num(r[c.key], `${c.cls} ${c.group ? "bg-slate-50/60" : ""}`))}
                  <td className={`px-3 py-3 text-right font-mono-fig font-semibold whitespace-nowrap bg-slate-50/60 ${r.netCash >= 0 ? "text-slate-900" : "text-red-600"}`}>{fmtRMSigned(r.netCash)}</td>
                  <td className={`px-5 py-3 text-right font-mono-fig font-semibold whitespace-nowrap bg-slate-50/60 ${profit >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`monthly-profit-${r.month}`}>{fmtRMSigned(profit)}</td>
                </tr>
              );
            })}
            {!loading && filteredRows.length === 0 && <tr><td colSpan={COLS.length + 3} className="px-5 py-12 text-center text-slate-400">No data for this period.</td></tr>}
            {loading && <tr><td colSpan={COLS.length + 3} className="px-5 py-12 text-center text-slate-400 animate-pulse">Loading…</td></tr>}
          </tbody>
          {filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-900 text-white font-semibold">
                <td className="px-5 py-3 sticky left-0 bg-slate-900 whitespace-nowrap">{year === "all" ? "Total" : `Total ${year}`}</td>
                {COLS.map((c) => (
                  <td key={c.key} className="px-3 py-3 text-right font-mono-fig whitespace-nowrap">{fmtRM(totals[c.key] || 0)}</td>
                ))}
                <td className="px-3 py-3 text-right font-mono-fig whitespace-nowrap">{fmtRMSigned(totals.netCash || 0)}</td>
                <td className="px-5 py-3 text-right font-mono-fig whitespace-nowrap" data-testid="monthly-profit-total">{fmtRMSigned(totals.netProfit || 0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Layout>
  );
}
